import React, { useState, useEffect, useRef } from 'react';
import API, { BACKEND_URL } from '../api';
import { HardHat, X, Volume2, VolumeX, Download, Trash2 } from 'lucide-react';
import MessageList from './assistant/MessageList';
import InputArea from './assistant/InputArea';

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem('geppo_voice') !== 'off');
  const [pendingAction, setPendingAction] = useState(null);
  const [config, setConfig] = useState(null);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const [attached, setAttached] = useState(null); // {upload_ref, nome_file, content_type, is_image}

  useEffect(() => {
    if (open && !config) {
      API.get('/api/assistant/config').then(r => setConfig(r.data)).catch(() => {});
    }
  }, [open, config]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pendingAction]);

  useEffect(() => {
    localStorage.setItem('geppo_voice', voiceOn ? 'on' : 'off');
  }, [voiceOn]);

  const playTTS = async (text) => {
    if (!voiceOn || !text) return;
    try {
      const resp = await API.post('/api/assistant/speak', { text }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'audio/mpeg' }));
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.play().catch(() => {});
      }
    } catch (e) { /* silenziato */ }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if ((!text && !attached) || loading) return;
    const uploadedRef = attached?.upload_ref || null;
    const attachedName = attached?.nome_file;
    setMessages(m => [...m, { role: 'user', content: text, upload_name: attachedName }]);
    setInput('');
    setAttached(null);
    setLoading(true);
    try {
      const payload = { message: text, session_id: sessionId };
      if (uploadedRef) payload.upload_ref = uploadedRef;
      const r = await API.post('/api/assistant/chat', payload);
      if (r.data.session_id && !sessionId) setSessionId(r.data.session_id);
      const msg = { role: 'assistant', content: r.data.message, action: r.data.action };
      setMessages(m => [...m, msg]);
      playTTS(r.data.message);
      if (r.data.action) {
        if (r.data.action.needs_confirmation) {
          setPendingAction(r.data.action);
        } else {
          await executeAction(r.data.action);
        }
      }
    } catch (e) {
      setMessages(m => [...m, { role: 'error', content: 'Errore: ' + (e.response?.data?.detail || e.message) }]);
    }
    setLoading(false);
  };

  const pickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await API.post('/api/assistant/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAttached(r.data);
    } catch (err) {
      alert('Errore upload: ' + (err.response?.data?.detail || err.message));
    }
    setLoading(false);
  };

  const executeAction = async (action) => {
    setPendingAction(null);
    setLoading(true);
    try {
      const r = await API.post('/api/assistant/execute', { action });
      const res = r.data.result || {};
      let summary = res.message || `Azione '${action.type}' eseguita`;
      if (res.download_url && res.download_url.startsWith('mailto:')) {
        // Apri client email invece di scaricare
        window.location.href = res.download_url;
        summary = `Email aperta nel tuo client (${res.destinatario || ''})`;
      } else if (res.download_url) {
        // Trigger download
        const apiBase = BACKEND_URL;
        const token = sessionStorage.getItem('edilcima_token');
        try {
          const fileResp = await fetch(apiBase + res.download_url, { headers: { Authorization: `Bearer ${token}` } });
          const blob = await fileResp.blob();
          const disp = fileResp.headers.get('content-disposition') || '';
          const match = disp.match(/filename="?([^";]+)"?/);
          const fname = match ? match[1] : `file_${Date.now()}`;
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = fname; a.click();
          URL.revokeObjectURL(url);
          summary = `File '${fname}' scaricato`;
        } catch (e) { summary += ' (download manuale richiesto)'; }
      }
      setMessages(m => [...m, { role: 'action_result', content: summary, data: res }]);
      playTTS(summary);
    } catch (e) {
      const err = 'Errore: ' + (e.response?.data?.detail || e.message);
      setMessages(m => [...m, { role: 'error', content: err }]);
    }
    setLoading(false);
  };

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const fd = new FormData();
        fd.append('file', blob, 'voice.webm');
        setLoading(true);
        try {
          const r = await API.post('/api/assistant/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const text = r.data.text || '';
          if (text) {
            setInput(text);
            await send(text);
          }
        } catch (e) {
          setMessages(m => [...m, { role: 'error', content: 'Errore trascrizione audio' }]);
        }
        setLoading(false);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      alert('Accesso microfono negato');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const resetChat = () => {
    setMessages([]);
    setSessionId(null);
    setPendingAction(null);
  };

  const emoji = config?.emoji || '👷';
  const nome = config?.nome_assistente || 'Geppo';

  return (
    <>
      <audio ref={audioRef} className="hidden" />

      {/* Floating button */}
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="open-assistant-btn"
          className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-primary hover:scale-105 transition-transform shadow-lg flex items-center justify-center group"
          title={`Apri ${nome}`}>
          <span className="text-2xl">{emoji}</span>
          <span className="absolute bottom-full right-0 mb-2 bg-slate-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
            Parla con {nome}
          </span>
          <span className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-[60] w-[380px] max-w-[calc(100vw-40px)] h-[600px] max-h-[calc(100vh-40px)] bg-white rounded-md shadow-2xl border border-slate-200 flex flex-col animate-fade-in"
          data-testid="assistant-panel">
          {/* Header */}
          <div className="bg-slate-900 text-white px-4 py-3 rounded-t-md flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-xl">
                {emoji}
              </div>
              <div>
                <p className="font-black text-sm">{nome}</p>
                <p className="text-[10px] text-slate-300 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block" />
                  Online · {config?.voce_modello || 'onyx'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setVoiceOn(v => !v)} title={voiceOn ? "Disattiva voce" : "Attiva voce"} data-testid="toggle-voice"
                className="p-1.5 hover:bg-white/10 rounded">
                {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </button>
              <button onClick={resetChat} title="Nuova conversazione" data-testid="reset-chat"
                className="p-1.5 hover:bg-white/10 rounded">
                <Trash2 size={14} />
              </button>
              <button onClick={() => setOpen(false)} data-testid="close-assistant"
                className="p-1.5 hover:bg-white/10 rounded">
                <X size={16} />
              </button>
            </div>
          </div>

          <MessageList
            messages={messages}
            nome={nome}
            emoji={emoji}
            loading={loading}
            pendingAction={pendingAction}
            bottomRef={bottomRef}
            onSend={send}
            onConfirmAction={() => executeAction(pendingAction)}
            onCancelAction={() => setPendingAction(null)}
          />

          <InputArea
            fileInputRef={fileInputRef}
            attached={attached}
            onClearAttached={() => setAttached(null)}
            onPickFile={pickFile}
            onFileChange={onFileChange}
            recording={recording}
            onStartRecording={startRecording}
            onStopRecording={stopRecording}
            input={input}
            setInput={setInput}
            onSend={send}
            loading={loading}
            nome={nome}
          />
        </div>
      )}
    </>
  );
}

// MessageBubble e Suggestion sono stati estratti in components/assistant/MessageBubble.jsx
// durante il refactor del 9 Maggio 2026 (riduzione complessità AssistantWidget).
